import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { assetsApi, inspectionsApi, depotsApi, certificatesApi, componentsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useLookup, useEquipmentMakes, useEquipmentModels } from '../hooks/useLookups';
import TelemetryCard from '../components/assets/TelemetryCard';
import AssetComplianceCard from '../components/assets/AssetComplianceCard';
import { ReplacesPicker, ReplacementLinks, isSuperseded } from '../components/certificates/CertificateReplacement';
import { AssetErrorCodeLibrariesCard } from '../components/AssetErrorCodeLibrariesCard';
import { AssetPublicInspectionsCard } from '../components/AssetPublicInspectionsCard';
import { ColumnPicker } from '../components/ColumnPicker';
import { Card, CardStack, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { StatusPill } from '../components/ui/StatusPill';
import { ReadinessDot, ReadinessBadge, GearChip, READINESS_META } from '../components/ReadinessBadge';
import ReadinessSummaryBanner from '../components/ReadinessSummaryBanner';
import { BulkDepotDialog, BulkReadinessDialog, BulkScheduleDialog, exportAssetsCsv } from '../components/assets/AssetBulkActions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SlideDrawer } from '../components/ui/slide-drawer';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { useConfirm } from '../components/useConfirm';
import { ActionMenu } from '../components/ui/action-menu';
import { PlanLimitBanner } from '../components/PlanLimitBanner';
import { 
  Plus, 
  Search, 
  Package, 
  Edit, 
  Trash2, 
  QrCode, 
  ClipboardCheck,
  History,
  Loader2,
  AlertTriangle,
  Download,
  LayoutGrid,
  List,
  X,
  Eye,
  MapPin,
  Camera,
  ImageIcon,
  Award,
  Cog,
  ArrowRightLeft,
  Upload,
  FileText,
  Pencil,
  Globe,
  Lock,
  Link as LinkIcon,
  Link2,
  Radio,
  Satellite,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  Rows3,
  Rows2,
  Square,
  Copy,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';

// Helper function to get image URL with token for Azure Blob proxy
const getImageSrc = (img, token) => {
  if (!img) return '';
  if (img.startsWith('data:')) return img;
  if (img.includes('blob.core.windows.net')) {
    const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
    return `${process.env.REACT_APP_BACKEND_URL}/api/blob-proxy?url=${encodeURIComponent(img)}${tokenParam}`;
  }
  if (img.startsWith('http://') || img.startsWith('https://')) return img;
  return `data:image/jpeg;base64,${img}`;
};

// Dynamic StatusBadge using lookup color
const StatusBadge = ({ status, statuses }) => {
  const statusItem = statuses?.find((s) => s.value === status);
  const label = statusItem?.name || status?.replace(/_/g, ' ') || 'Unknown';
  // Fix 2: status marks EXCEPTIONS only. Active = quiet dot + label, no pill.
  // Out of Service = orange pill. Anything else falls back to the tinted pill.
  const isOOS = status === 'out_of_service' || /out.of.service/i.test(label);
  const isActive = status === 'active' || /^active$/i.test(label);
  if (isActive) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border bg-[#ECFDF5] text-[#047857] border-[#A7F3D0] capitalize"
        data-testid={`status-pill-${status}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-[#047857]" />
        {label}
      </span>
    );
  }
  if (isOOS) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full text-[12px] font-semibold capitalize"
        style={{ background: '#FFEDE4', border: '1px solid #FFC9AE', color: '#8F2E00', padding: '2px 8px' }}
        data-testid={`status-pill-${status}`}>
        <span className="rounded-full shrink-0" style={{ width: 5, height: 5, background: '#FF4F00' }} />
        {label}
      </span>
    );
  }
  return <StatusPill value={status} label={label} size="sm" />;
};

// Telematics IMEI status pill — surfaces assets wired up to a TCP GPS gateway.
// Truncates the middle of the IMEI so the badge stays compact (35265…34567).
// Theme-aware: uses a darker amber tint in light mode for readability, the
// original volt-yellow chip in dark mode.
const VdaqPill = ({ asset }) => {
  if (asset?.telemetry_source !== 'vdaq') return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-sm text-[9px] font-mono uppercase tracking-wide
                 bg-[#EAF2FF] text-[#1F4E8C] border border-[#C3DAFB]"
      style={{ padding: '1px 4px' }}
      title={`Linked to V-DAQ${asset.vdaq_registration ? ` · ${asset.vdaq_registration}` : ''}`}
      data-testid={`asset-vdaq-pill-${asset.id}`}
    >
      <Satellite className="w-2 h-2" />
      via V-DAQ{asset.vdaq_registration ? ` · ${asset.vdaq_registration}` : ''}
    </span>
  );
};

const ImeiPill = ({ imei }) => {
  if (!imei) return null;
  const display =
    imei.length > 9 ? `${imei.slice(0, 5)}…${imei.slice(-5)}` : imei;
  return (
    <span
      className="imei-pill inline-flex items-center gap-1 rounded-sm text-[9px] font-mono uppercase tracking-wide
                 bg-[#FFF6E0] text-[#8A5A0F] border border-[#EFD9A3]"
      style={{ padding: '1px 4px' }}
      title="Telematics connected"
      data-testid={`asset-imei-pill-${imei}`}
    >
      <Radio className="w-2 h-2" />
      IMEI · {display}
    </span>
  );
};

// Render a meter value with the right unit label. Cranes typically have two
// meters: a primary (KM/miles, the bottom unit on the dash) and a secondary
// (engine hours, the top unit) — both with their own units. For other assets
// the secondary meter is null and we just render the primary.
const METER_UNIT_LABEL = {
  hours: 'hrs',
  km: 'km',
  miles: 'mi',
};
const meterUnitLabel = (u) => METER_UNIT_LABEL[u] || 'hrs';

const formatMeter = (value, unit) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString()} ${meterUnitLabel(unit)}`;
};

// Compact pair: "12,400 km · 4,820 hrs" — used inline on dense asset rows.
const formatMeters = (asset) => {
  if (!asset) return '—';
  const primary = formatMeter(asset.hours ?? 0, asset.hours_unit || 'hours');
  if (asset.secondary_meter != null && asset.secondary_meter !== '') {
    return `${primary} · ${formatMeter(asset.secondary_meter, asset.secondary_meter_unit || 'hours')}`;
  }
  return primary;
};



// Density → row vertical padding. Compact halves the row height vs Comfortable
// so a 50-asset fleet fits on one viewport on a 14" laptop without scrolling.
const densityClass = (density) => {
  if (density === 'compact') return 'px-4 py-1.5';
  if (density === 'comfortable') return 'px-4 py-5';
  return 'px-4 py-3'; // cosy (default)
};

// Sortable column header — click to cycle: asc → desc → (column stays, dir flips).
// Renders the active direction arrow, neutral chevrons when inactive.
const SortableTh = ({ sortKey, label, current, dir, onSort, align = 'left' }) => {
  const active = current === sortKey;
  const Arrow = active ? (dir === 'desc' ? ArrowDown : ArrowUp) : ChevronsUpDown;
  return (
    <th className={`text-${align} p-4`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-[11px] transition-colors ${
          active ? 'text-[#FFD108]' : 'text-muted-foreground hover:text-foreground'
        }`}
        data-testid={`asset-sort-${sortKey}`}
        aria-label={`Sort by ${label} ${active ? (dir === 'desc' ? 'descending' : 'ascending') : ''}`}
      >
        {label}
        <Arrow className={`w-3 h-3 ${active ? '' : 'opacity-50'}`} />
      </button>
    </th>
  );
};


// Asset Form Component for Add/Edit
const AssetForm = ({ asset, categories, statuses, makes, models, depots = [], onSuccess, onCancel, token }) => {
  const isEdit = !!asset?.id;
  const [saving, setSaving] = useState(false);
  const [selectedMake, setSelectedMake] = useState(asset?.make || '');
  const [formData, setFormData] = useState({
    unit_number: asset?.unit_number || '',
    name: asset?.name || '',
    category: asset?.category || (categories[0]?.value || 'crane'),
    custom_type: asset?.custom_type || '',
    serial_number: asset?.serial_number || '',
    make: asset?.make || '',
    model: asset?.model || '',
    year: asset?.year || new Date().getFullYear(),
    status: asset?.status || (statuses[0]?.value || 'active'),
    notes: asset?.notes || '',
    hours: asset?.hours || 0,
    hours_unit: asset?.hours_unit || 'hours',
    secondary_meter: asset?.secondary_meter ?? '',
    secondary_meter_unit: asset?.secondary_meter_unit || 'hours',
    depot_id: asset?.depot_id || '',
    images: asset?.images || [],
    asset_class: asset?.asset_class || 'main',
    parent_asset_id: asset?.parent_asset_id || '',
    swl: asset?.swl || '',
    swl_unit: asset?.swl_unit || 'tonnes',
    vin: asset?.vin || '',
    registration: asset?.registration || '',
  });
  const [newImages, setNewImages] = useState([]);

  // Filter models by selected make
  const filteredModels = useMemo(() => {
    if (!selectedMake) return models;
    const make = makes.find(m => m.name.toLowerCase() === selectedMake.toLowerCase());
    if (!make) return models;
    return models.filter(m => m.make_id === make.id);
  }, [selectedMake, makes, models]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'make') {
      setSelectedMake(value);
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMakeSelect = (makeName) => {
    setFormData(prev => ({ ...prev, make: makeName, model: '' }));
    setSelectedMake(makeName);
  };

  const handleModelSelect = (modelName) => {
    setFormData(prev => ({ ...prev, model: modelName }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const totalImages = (formData.images?.length || 0) + newImages.length + files.length;
    
    if (totalImages > 5) {
      toast.error('Maximum 5 images allowed per asset');
      return;
    }
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewImages(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Combine existing and new images
      const allImages = [...(formData.images || []), ...newImages];
      const submitData = { ...formData, images: allImages };
      
      // Handle SWL as number
      if (submitData.swl) submitData.swl = parseFloat(submitData.swl);
      else submitData.swl = null;
      
      // Clean parent_asset_id
      if (!submitData.parent_asset_id) submitData.parent_asset_id = null;

      // Secondary meter — empty string → null. Backend rejects empty strings on
      // the optional float field.
      if (submitData.secondary_meter === '' || submitData.secondary_meter == null) {
        submitData.secondary_meter = null;
        submitData.secondary_meter_unit = null;
      } else {
        submitData.secondary_meter = parseFloat(submitData.secondary_meter);
      }
      // Optional identifiers — blank clears the stored value.
      submitData.vin = (submitData.vin || '').trim() || null;
      submitData.registration = (submitData.registration || '').trim() || null;
      
      if (isEdit) {
        await assetsApi.update(asset.id, submitData);
        toast.success('Asset updated');
      } else {
        await assetsApi.create(submitData);
        toast.success('Asset created');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="as-card p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="unit_number">Unit # *</Label>
          <Input
            id="unit_number"
            name="unit_number"
            value={formData.unit_number}
            onChange={handleChange}
            placeholder="e.g. CR-001"
            required
            className="input-industrial"
            data-testid="asset-unit-number-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Description</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. Liebherr LTM 1100"
            className="input-industrial"
            data-testid="asset-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="serial_number">Serial Number *</Label>
          <Input
            id="serial_number"
            name="serial_number"
            value={formData.serial_number}
            onChange={handleChange}
            placeholder="e.g. SN-12345"
            required
            className="input-industrial"
            data-testid="asset-serial-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="vin">VIN</Label>
            <Input id="vin" name="vin" value={formData.vin} onChange={handleChange} maxLength={32}
              placeholder="Optional" className="input-industrial uppercase" data-testid="asset-vin-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="registration">Registration (rego)</Label>
            <Input id="registration" name="registration" value={formData.registration} onChange={handleChange} maxLength={20}
              placeholder="Optional" className="input-industrial uppercase" data-testid="asset-registration-input" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Category *</Label>
          <Select value={formData.category} onValueChange={(v) => handleSelectChange('category', v)}>
            <SelectTrigger className="input-industrial" data-testid="asset-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {formData.category === 'custom' && (
          <div className="space-y-2">
            <Label htmlFor="custom_type">Custom Type</Label>
            <Input
              id="custom_type"
              name="custom_type"
              value={formData.custom_type}
              onChange={handleChange}
              placeholder="Specify type"
              className="input-industrial"
              data-testid="asset-custom-type-input"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label>Asset Class *</Label>
          <Select value={formData.asset_class} onValueChange={(v) => handleSelectChange('asset_class', v)}>
            <SelectTrigger className="input-industrial" data-testid="asset-class-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="main">Main Asset</SelectItem>
              <SelectItem value="auxiliary">Auxiliary Equipment</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.asset_class === 'auxiliary' && (
          <>
            <div className="space-y-2">
              <Label>Attached To (Parent Asset)</Label>
              <Select value={formData.parent_asset_id || 'none'} onValueChange={(v) => handleSelectChange('parent_asset_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="input-industrial" data-testid="parent-asset-select">
                  <SelectValue placeholder="Unattached" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unattached (In Store)</SelectItem>
                  {/* Note: parent options would need main assets passed in. Using empty for now - backend handles validation */}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Leave unattached if stored independently</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label>SWL (Safe Working Load)</Label>
                <Input
                  type="number"
                  name="swl"
                  value={formData.swl}
                  onChange={handleChange}
                  placeholder="e.g. 5"
                  className="input-industrial"
                  data-testid="asset-swl-input"
                />
              </div>
              <div className="space-y-2">
                <Label>SWL Unit</Label>
                <Select value={formData.swl_unit} onValueChange={(v) => handleSelectChange('swl_unit', v)}>
                  <SelectTrigger className="input-industrial"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tonnes">Tonnes</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="lbs">Lbs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </>
        )}

        <div className="space-y-2">
          <Label>Status *</Label>
          <Select value={formData.status} onValueChange={(v) => handleSelectChange('status', v)}>
            <SelectTrigger className="input-industrial" data-testid="asset-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {statuses.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Make *</Label>
          {makes.length > 0 ? (
            <Select value={formData.make} onValueChange={handleMakeSelect}>
              <SelectTrigger className="input-industrial" data-testid="asset-make-select">
                <SelectValue placeholder="Select make" />
              </SelectTrigger>
              <SelectContent>
                {makes.map(m => (
                  <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="make"
              name="make"
              value={formData.make}
              onChange={handleChange}
              placeholder="e.g. Caterpillar"
              required
              className="input-industrial"
              data-testid="asset-make-input"
            />
          )}
        </div>

        <div className="space-y-2">
          <Label>Model *</Label>
          {filteredModels.length > 0 ? (
            <Select value={formData.model} onValueChange={handleModelSelect}>
              <SelectTrigger className="input-industrial" data-testid="asset-model-select">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {filteredModels.map(m => (
                  <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              id="model"
              name="model"
              value={formData.model}
              onChange={handleChange}
              placeholder="e.g. 320D"
              required
              className="input-industrial"
              data-testid="asset-model-input"
            />
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="year">Year *</Label>
          <Input
            id="year"
            name="year"
            type="number"
            value={formData.year}
            onChange={handleChange}
            min="1900"
            max={new Date().getFullYear() + 1}
            required
            className="input-industrial"
            data-testid="asset-year-input"
          />
        </div>

        {/* Primary meter — typically KM/miles for cranes (bottom unit on the dash)
            or engine hours for everything else. Defaults to "hours" for legacy. */}
        <div className="space-y-2 md:col-span-2">
          <Label>Primary meter reading</Label>
          <div className="flex gap-2">
            <Input
              id="hours"
              name="hours"
              type="number"
              value={formData.hours}
              onChange={handleChange}
              min="0"
              step="0.1"
              className="input-industrial flex-1"
              placeholder="0"
              data-testid="asset-hours-input"
            />
            <Select
              value={formData.hours_unit || 'hours'}
              onValueChange={(v) => handleSelectChange('hours_unit', v)}
            >
              <SelectTrigger className="input-industrial w-32" data-testid="asset-hours-unit-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="km">Kilometres</SelectItem>
                <SelectItem value="miles">Miles</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Cranes typically show distance here (bottom unit on the dash).
          </p>
        </div>

        {/* Secondary meter — optional. Useful for cranes which have a second
            dash unit (engine hours) alongside the primary distance reading. */}
        <div className="space-y-2 md:col-span-2">
          <Label>Secondary meter reading <span className="text-muted-foreground text-xs font-normal">(optional — cranes)</span></Label>
          <div className="flex gap-2">
            <Input
              id="secondary_meter"
              name="secondary_meter"
              type="number"
              value={formData.secondary_meter}
              onChange={handleChange}
              min="0"
              step="0.1"
              className="input-industrial flex-1"
              placeholder="Leave blank if not applicable"
              data-testid="asset-secondary-meter-input"
            />
            <Select
              value={formData.secondary_meter_unit || 'hours'}
              onValueChange={(v) => handleSelectChange('secondary_meter_unit', v)}
            >
              <SelectTrigger className="input-industrial w-32" data-testid="asset-secondary-meter-unit-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="km">Kilometres</SelectItem>
                <SelectItem value="miles">Miles</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Depot Location</Label>
          <Select 
            value={formData.depot_id || "unassigned"} 
            onValueChange={(v) => handleSelectChange('depot_id', v === "unassigned" ? null : v)}
          >
            <SelectTrigger className="input-industrial" data-testid="asset-depot-select">
              <SelectValue placeholder="Select depot" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  Unassigned
                </span>
              </SelectItem>
              {depots.map(d => (
                <SelectItem key={d.id} value={d.id}>
                  <span className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    {d.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Asset Images Section */}
      <div className="space-y-3">
        <Label>Asset Images (max 5)</Label>
        <div className="grid grid-cols-5 gap-3">
          {/* Existing images */}
          {formData.images?.map((img, index) => (
            <div key={`existing-${index}`} className="relative aspect-square rounded-lg overflow-hidden bg-secondary group">
              <img 
                src={getImageSrc(img, token)}
                alt={`Asset ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeExistingImage(index)}
                className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
          
          {/* New images */}
          {newImages.map((img, index) => (
            <div key={`new-${index}`} className="relative aspect-square rounded-lg overflow-hidden bg-secondary group">
              <img 
                src={img}
                alt={`New ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => removeNewImage(index)}
                className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ))}
          
          {/* Add image button */}
          {((formData.images?.length || 0) + newImages.length) < 5 && (
            <label className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
              <Camera className="w-6 h-6 text-muted-foreground mb-1" />
              <span className="text-xs text-muted-foreground">Add</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {(formData.images?.length || 0) + newImages.length}/5 images uploaded
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          placeholder="Additional notes about this asset..."
          rows={3}
          data-testid="asset-notes-input"
        />
      </div>
      </div>

      <div className="flex gap-3 pt-3.5 pb-4 sticky bottom-0 bg-white border-t border-[#DAD5CA] mt-4 -mx-6 px-6">
        <button
          type="submit"
          disabled={saving}
          data-testid="save-asset-btn"
          className="flex-1 inline-flex items-center justify-center h-10 rounded-[8px] text-[13px] font-bold uppercase tracking-[0.04em] bg-[#FFD108] border border-[#E0B800] text-black hover:bg-[#E5B93D] transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {saving ? 'Saving...' : isEdit ? 'Update Asset' : 'Create Asset'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="cancel-btn"
          className="inline-flex items-center justify-center h-10 px-5 rounded-[8px] text-[13px] font-semibold bg-white border border-[#C9C3B6] text-[#1A1A1A] hover:bg-[#FBFAF7] transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

// ─── Certificates Tab Component ─────────────────────────────────────
const CertificatesTab = ({ assetId, prefill, onPrefillConsumed }) => {
  const { formatDate } = useSettings();
  const { confirm, dialogProps } = useConfirm();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  // `editingId` non-null when the form is reused to edit an existing cert
  // (vs. creating a new one). Keeps the UI deliberately single-form so users
  // never have multiple inline editors fighting for attention.
  const [editingId, setEditingId] = useState(null);
  const [newCert, setNewCert] = useState({
    certificate_type: '', issuing_body: '', issue_date: '', expiry_date: '',
    notes: '', standard_id: null,
  });
  // File to upload (or replace) when the form is saved. Held as base64 +
  // metadata since the backend expects a base64 payload.
  const [pendingFile, setPendingFile] = useState(null); // { dataUrl, base64, name, contentType, size }
  const [replacesId, setReplacesId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const loadCerts = async () => {
    try {
      const res = await certificatesApi.list({ asset_id: assetId });
      setCerts(res.data || []);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { loadCerts(); }, [assetId]);

  useEffect(() => {
    if (!prefill) return;
    setNewCert((prev) => ({
      ...prev,
      certificate_type: prefill.certificate_type || prev.certificate_type,
      standard_id: prefill.standard_id || null,
    }));
    setEditingId(null);
    setShowAdd(true);
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed]);

  // Open the form to EDIT an existing certificate.
  const handleEdit = (cert) => {
    setNewCert({
      certificate_type: cert.certificate_type || '',
      issuing_body: cert.issuing_body || '',
      issue_date: cert.issue_date ? cert.issue_date.split('T')[0] : '',
      expiry_date: cert.expiry_date ? cert.expiry_date.split('T')[0] : '',
      notes: cert.notes || '',
      standard_id: cert.standard_id || null,
    });
    setEditingId(cert.id);
    setPendingFile(null);
    setShowAdd(true);
  };

  // Close + reset the inline form back to "Add" mode.
  const closeForm = () => {
    setShowAdd(false);
    setEditingId(null);
    setPendingFile(null);
    setReplacesId(null);
    setNewCert({ certificate_type: '', issuing_body: '', issue_date: '', expiry_date: '', notes: '', standard_id: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Read the picked file as base64 so it can be POSTed to the upload endpoint.
  const handleFilePicked = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large — 10 MB max.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      // Strip the `data:type;base64,` prefix so backend gets pure base64.
      const base64 = dataUrl.split(',')[1] || '';
      setPendingFile({
        dataUrl, base64, name: file.name, contentType: file.type, size: file.size,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!newCert.certificate_type) { toast.error('Certificate type is required'); return; }
    setSaving(true);
    try {
      let certId = editingId;
      if (editingId) {
        await certificatesApi.update(editingId, newCert);
      } else {
        const res = await certificatesApi.create({ ...newCert, asset_id: assetId, replaces_cert_id: replacesId || null });
        certId = res.data?.id;
      }
      if (pendingFile && certId) {
        await certificatesApi.uploadFile(
          certId, pendingFile.base64, pendingFile.name, pendingFile.contentType,
        );
      }
      toast.success(editingId ? 'Certificate updated' : 'Certificate added');
      closeForm();
      loadCerts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save certificate');
    } finally {
      setSaving(false);
    }
  };

  // Open the attached file in a new tab. We fetch it as a blob (with auth)
  // then create an object URL — naive window.open(/file) would 401 because
  // the browser doesn't propagate the bearer token across navigations.
  const handleView = async (cert) => {
    try {
      const res = await certificatesApi.downloadFile(cert.id);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      // Revoke the blob URL after a short delay so memory is freed but the
      // tab has time to load it.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error('Could not open file');
    }
  };

  const handleDelete = (cert) => {
    confirm({
      title: 'Delete this certificate?',
      description: `${cert.cert_number} will be permanently removed.`,
      footnote: 'This cannot be undone. Any attached file is also deleted.',
      testId: 'confirm-delete-certificate',
      onConfirm: async () => {
        try {
          await certificatesApi.delete(cert.id);
          toast.success('Certificate deleted');
          loadCerts();
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Failed to delete certificate');
          throw err;
        }
      },
    });
  };

  // Phase 50: toggle public-QR visibility per-certificate.
  const handleTogglePublic = async (cert) => {
    const nextValue = !cert.is_public;
    try {
      await certificatesApi.update(cert.id, { is_public: nextValue });
      toast.success(
        nextValue
          ? `${cert.cert_number} is now visible on the public QR page`
          : `${cert.cert_number} is no longer public`,
      );
      loadCerts();
    } catch (err) {
      toast.error('Failed to update certificate');
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const currentCerts = certs.filter(c => !isSuperseded(c));
  const historyCerts = certs.filter(isSuperseded);
  const shownCerts = showHistory ? [...currentCerts, ...historyCerts] : currentCerts;

  return (
    <div className="space-y-4" data-testid="certificates-tab-content">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {currentCerts.length} current certificate{currentCerts.length !== 1 ? 's' : ''}
          {historyCerts.length > 0 && (
            <button type="button" className="ml-3 underline underline-offset-2 hover:text-foreground" onClick={() => setShowHistory(v => !v)} data-testid="cert-history-toggle">
              {showHistory ? 'Hide' : 'Show'} {historyCerts.length} superseded
            </button>
          )}
        </p>
        <button type="button" className={BTN_VOLT} onClick={() => { if (showAdd) closeForm(); else setShowAdd(true); }} data-testid="add-certificate-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Certificate
        </button>
      </div>

      {showAdd && (
        <Card data-testid="certificate-form-card">
          <CardContent className="p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              {editingId ? 'Edit certificate' : 'New certificate'}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-overline">Type *</Label><Input value={newCert.certificate_type} onChange={e => setNewCert({...newCert, certificate_type: e.target.value})} placeholder="e.g. Annual Thorough Examination" data-testid="cert-type-input" /></div>
              <div><Label className="label-overline">Issuing Body</Label><Input value={newCert.issuing_body} onChange={e => setNewCert({...newCert, issuing_body: e.target.value})} placeholder="e.g. SafeLift Pty Ltd" /></div>
              <div><Label className="label-overline">Issue Date</Label><Input type="date" value={newCert.issue_date} onChange={e => setNewCert({...newCert, issue_date: e.target.value})} /></div>
              <div><Label className="label-overline">Expiry Date</Label><Input type="date" value={newCert.expiry_date} onChange={e => setNewCert({...newCert, expiry_date: e.target.value})} /></div>
            </div>
            <div><Label className="label-overline">Notes</Label><Input value={newCert.notes} onChange={e => setNewCert({...newCert, notes: e.target.value})} placeholder="Optional notes" /></div>
            {!editingId && (
              <ReplacesPicker certs={certs} certificateType={newCert.certificate_type} value={replacesId} onChange={setReplacesId} />
            )}

            {/* File attachment row */}
            <div>
              <Label className="label-overline">Attached File</Label>
              <div className="mt-1 flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => handleFilePicked(e.target.files?.[0])}
                  data-testid="cert-file-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="cert-file-pick-btn"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  {pendingFile ? 'Replace file' : 'Attach PDF or image'}
                </Button>
                {pendingFile && (
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-[#FFD108]" />
                    <span className="truncate max-w-[240px]">{pendingFile.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({(pendingFile.size / 1024).toFixed(0)} KB)
                    </span>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-red-400"
                      onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      aria-label="Remove pending file"
                    >
                      ×
                    </button>
                  </div>
                )}
                {!pendingFile && editingId && (
                  <span className="text-xs text-muted-foreground">
                    PDF, PNG, JPG or WebP — up to 10 MB. Existing file is preserved if you skip this.
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button size="sm" className="btn-volt" onClick={handleSave} disabled={saving} data-testid="cert-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? 'Save changes' : 'Save')}
              </Button>
              <Button size="sm" variant="ghost" onClick={closeForm}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {certs.length === 0 && !showAdd ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground"><Award className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No certificates yet</p></CardContent></Card>
      ) : (
        <CardStack>
        {shownCerts.map(cert => (
          <Card key={cert.id} className={`hover:border-primary/30 transition-colors ${isSuperseded(cert) ? 'opacity-70' : ''}`} data-testid={`cert-card-${cert.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-muted-foreground">{cert.cert_number}</p>
                  <p className="font-semibold flex items-center gap-2 flex-wrap">
                    {cert.certificate_type}
                    {cert.is_public && (
                      <span
                        title="Visible on the public QR-scan page"
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#8A6D00] bg-[#FFF6E0] border border-[#EFD9A3] px-1.5 py-0.5 rounded"
                      >
                        <Globe className="w-3 h-3" /> Public
                      </span>
                    )}
                  </p>
                  {cert.issuing_body && <p className="text-sm text-muted-foreground">{cert.issuing_body}</p>}
                  {cert.has_file && (
                    <p className="text-xs text-[#8A6D00] mt-1 flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {cert.document_name || 'File attached'}
                    </p>
                  )}
                </div>
                {(() => {
                  const cs = ['valid', 'expiring', 'expired', 'superseded'].includes(cert.status)
                    ? cert.status
                    : (cert.expiry_date && new Date(cert.expiry_date) < new Date() ? 'expired' : 'valid');
                  const tone = cs === 'valid' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                    : cs === 'expiring' ? 'bg-amber-50 text-amber-700 ring-amber-200'
                    : cs === 'superseded' ? 'bg-stone-100 text-stone-600 ring-stone-300'
                    : 'bg-red-50 text-red-700 ring-red-200';
                  return (
                    <span
                      className={`inline-flex items-center h-6 px-2.5 rounded-full text-xs font-medium ring-1 ring-inset whitespace-nowrap ${tone}`}
                      data-testid={`cert-status-${cert.id}`}
                    >
                      {cs}
                    </span>
                  );
                })()}
              </div>
              <div className="flex gap-4 text-xs text-muted-foreground mb-3">
                {cert.issue_date && <span>Issued: <span className="font-mono">{formatDate(cert.issue_date)}</span></span>}
                {cert.expiry_date && <span>Expires: <span className="font-mono">{formatDate(cert.expiry_date)}</span></span>}
              </div>
              <ReplacementLinks cert={cert} certs={certs} onChanged={loadCerts} />
              {/* Action row — view + edit + make public (left) · delete (right) */}
              <div className="flex items-center justify-between gap-2 pt-3 mt-3 border-t border-[#F0EDE6]">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleView(cert)}
                    disabled={!cert.has_file}
                    className={BTN_OUTLINE}
                    data-testid={`cert-view-btn-${cert.id}`}
                    title={cert.has_file ? 'Open attached file' : 'No file attached'}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> View
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEdit(cert)}
                    className={BTN_OUTLINE}
                    data-testid={`cert-edit-btn-${cert.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTogglePublic(cert)}
                    disabled={isSuperseded(cert)}
                    className={cert.is_public ? BTN_VOLT_OUTLINE : BTN_OUTLINE}
                    data-testid={`cert-public-btn-${cert.id}`}
                    title={cert.is_public ? 'Hide from public QR page' : 'Show on public QR page'}
                  >
                    {cert.is_public ? <Lock className="w-3.5 h-3.5 mr-1.5" /> : <Globe className="w-3.5 h-3.5 mr-1.5" />}
                    {cert.is_public ? 'Public' : 'Make Public'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(cert)}
                  className={BTN_DANGER}
                  data-testid={`cert-delete-btn-${cert.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </button>
              </div>
            </CardContent>
          </Card>
        ))}
        </CardStack>
      )}
      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};

// ─── Components Tab Component ───────────────────────────────────────
const ComponentsTab = ({ assetId, assetName }) => {
  const [components, setComponents] = useState([]);
  const [auxAssets, setAuxAssets] = useState([]); // linked auxiliary assets
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showLinkAux, setShowLinkAux] = useState(false);
  const [auxPicker, setAuxPicker] = useState({ search: '', loading: false, options: [], saving: null });
  const [newComp, setNewComp] = useState({ component_type: '', description: '', serial_number: '', manufacturer: '', swl: '', swl_unit: 'tonnes' });
  const [saving, setSaving] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null); // asset object pending unlink confirm
  const [unlinking, setUnlinking] = useState(false);
  const { confirm, dialogProps } = useConfirm();

  const loadComponents = async () => {
    try {
      const res = await componentsApi.list({ parent_asset_id: assetId });
      setComponents(res.data || []);
    } catch { /* empty */ }
  };

  // Fetch auxiliary assets currently linked to this asset (parent_asset_id = us)
  const loadLinkedAux = async () => {
    try {
      const res = await assetsApi.getAll();
      const all = res.data || [];
      const linked = all.filter(a => a.parent_asset_id === assetId && a.asset_class === 'auxiliary');
      setAuxAssets(linked);
    } catch { /* empty */ }
  };

  // Pull all auxiliary assets in the tenant so the user can pick from them.
  // We include already-parented aux too — but annotate them with their
  // current parent's label and offer a "Reassign" action. Without this the
  // picker felt broken when users couldn't find an aux they knew existed
  // (because it was already linked to a *different* parent).
  const loadAuxOptions = async () => {
    setAuxPicker(p => ({ ...p, loading: true }));
    try {
      const res = await assetsApi.getAll();
      const all = res.data || [];

      // Build a lookup so we can label "currently linked to <X>" without
      // forcing another API round-trip per row.
      const byId = Object.fromEntries(all.map(a => [a.id, a]));

      const options = all
        .filter(a => a.asset_class === 'auxiliary' && a.id !== assetId)
        // Skip aux already linked to THIS asset (they're rendered above
        // in the "Linked auxiliary assets" list).
        .filter(a => a.parent_asset_id !== assetId)
        .map(a => ({
          ...a,
          _currentParent: a.parent_asset_id ? byId[a.parent_asset_id] : null,
        }))
        .sort((a, b) => (a.unit_number || a.name || '').localeCompare(b.unit_number || b.name || ''));
      setAuxPicker(p => ({ ...p, options, loading: false }));
    } catch (e) {
      setAuxPicker(p => ({ ...p, loading: false }));
      toast.error('Failed to load auxiliary assets');
    }
  };

  useEffect(() => {
    Promise.all([loadComponents(), loadLinkedAux()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  useEffect(() => {
    if (showLinkAux) loadAuxOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLinkAux]);

  const handleAdd = async () => {
    if (!newComp.component_type || !newComp.description) { toast.error('Type and description required'); return; }
    setSaving(true);
    try {
      await componentsApi.create({ ...newComp, parent_asset_id: assetId, swl: newComp.swl ? parseFloat(newComp.swl) : null });
      toast.success('Component added');
      setShowAdd(false);
      setNewComp({ component_type: '', description: '', serial_number: '', manufacturer: '', swl: '', swl_unit: 'tonnes' });
      loadComponents();
    } catch (err) { toast.error('Failed to add component'); }
    finally { setSaving(false); }
  };

  const linkAuxiliary = async (auxAsset) => {
    setAuxPicker(p => ({ ...p, saving: auxAsset.id }));
    try {
      await assetsApi.update(auxAsset.id, { parent_asset_id: assetId });
      toast.success(`Linked ${auxAsset.unit_number || auxAsset.name}`);
      await loadLinkedAux();
      // Remove from picker so it doesn't show twice.
      setAuxPicker(p => ({ ...p, options: p.options.filter(o => o.id !== auxAsset.id), saving: null }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link auxiliary asset');
      setAuxPicker(p => ({ ...p, saving: null }));
    }
  };

  const unlinkAuxiliary = async () => {
    if (!unlinkTarget) return;
    setUnlinking(true);
    try {
      await assetsApi.update(unlinkTarget.id, { parent_asset_id: null });
      toast.success(`Unlinked ${unlinkTarget.unit_number || unlinkTarget.name}`);
      setUnlinkTarget(null);
      loadLinkedAux();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unlink');
    } finally {
      setUnlinking(false);
    }
  };

  const filteredAuxOptions = useMemo(() => {
    const q = auxPicker.search.trim().toLowerCase();
    if (!q) return auxPicker.options;
    return auxPicker.options.filter(a =>
      [a.unit_number, a.name, a.serial_number, a.make, a.model]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [auxPicker.options, auxPicker.search]);

  const componentTypeLabels = {
    boom: 'Boom', wire_rope: 'Wire Rope', hook_block: 'Hook Block', sheave: 'Sheave',
    winch: 'Winch', slew_ring: 'Slew Ring', outrigger: 'Outrigger', counterweight: 'Counterweight',
    sling: 'Sling', shackle: 'Shackle', chain: 'Chain', hook: 'Hook',
    jib: 'Jib', structural_frame: 'Structural Frame', turntable: 'Turntable',
    platform: 'Platform', hydraulic_cylinder: 'Hydraulic Cylinder', control_system: 'Control System',
    eyebolt: 'Eyebolt', turnbuckle: 'Turnbuckle', spreader_bar: 'Spreader Bar',
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <CardStack data-testid="components-tab-content">
      {/* ── Linked Auxiliary Assets ────────────────────────────────────── */}
      <div className="as-card p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="as-section">
              <Package className="w-3.5 h-3.5 text-[#C9A400]" />
              Linked auxiliary assets
              <Badge className="lm-count ml-0.5 h-5 min-w-[20px] justify-center px-1.5 text-[11px]">{auxAssets.length}</Badge>
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Auxiliary assets (e.g. trailers, slings registered as assets) that ride with this unit.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowLinkAux(true)}
            data-testid="link-aux-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Link auxiliary
          </Button>
        </div>

        {auxAssets.length === 0 ? (
          <p className="text-[13px] text-muted-foreground text-center py-6 border border-dashed border-[#D9D4C8] rounded-lg">
            No auxiliary assets linked to this unit.
          </p>
        ) : (
          <div className="space-y-2">
            {auxAssets.map(aux => (
              <Card key={aux.id} className="hover:border-primary/30 transition-colors">
                <CardContent className="p-3 flex items-center gap-3" data-testid={`linked-aux-${aux.id}`}>
                  <Package className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{aux.unit_number || aux.name}</span>
                      <Badge variant="outline" className="text-[10px] uppercase">{aux.category}</Badge>
                      <Badge variant="outline" className="text-[10px] uppercase border-amber-500/40 text-amber-300">Auxiliary</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      <span className="font-mono">{aux.serial_number}</span>
                      {aux.make && ` · ${aux.make} ${aux.model || ''}`}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setUnlinkTarget(aux)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`unlink-aux-${aux.id}`}
                    title="Unlink this auxiliary asset"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ── Inventory Components (boom, slings, rope etc.) ─────────────── */}
      <div className="as-card p-6 space-y-3">
        <div className="flex items-center justify-between">
        <div>
          <h3 className="as-section">
            <Cog className="w-3.5 h-3.5 text-[#C9A400]" />
            Inventory components
            <Badge className="lm-count ml-0.5 h-5 min-w-[20px] justify-center px-1.5 text-[11px]">{components.length}</Badge>
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Non-asset items (rope, slings, shackles…) attached to this unit.
          </p>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)} data-testid="add-component-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add component
        </Button>
      </div>

      {showAdd && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-overline">Type *</Label>
                <Select value={newComp.component_type} onValueChange={v => setNewComp({...newComp, component_type: v})}>
                  <SelectTrigger data-testid="comp-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(componentTypeLabels).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="label-overline">Serial #</Label><Input value={newComp.serial_number} onChange={e => setNewComp({...newComp, serial_number: e.target.value})} placeholder="S/N" /></div>
            </div>
            <div><Label className="label-overline">Description *</Label><Input value={newComp.description} onChange={e => setNewComp({...newComp, description: e.target.value})} placeholder="e.g. Main hoist wire rope 28mm" data-testid="comp-desc-input" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="label-overline">Manufacturer</Label><Input value={newComp.manufacturer} onChange={e => setNewComp({...newComp, manufacturer: e.target.value})} /></div>
              <div><Label className="label-overline">SWL</Label><Input type="number" value={newComp.swl} onChange={e => setNewComp({...newComp, swl: e.target.value})} placeholder="100" /></div>
              <div>
                <Label className="label-overline">Unit</Label>
                <Select value={newComp.swl_unit} onValueChange={v => setNewComp({...newComp, swl_unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tonnes">Tonnes</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="lbs">Lbs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="btn-volt" onClick={handleAdd} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {components.length === 0 && !showAdd ? (
        <div className="rounded-lg bg-[#FBFAF7] border border-[#E2DED4] py-10 text-center">
          <Cog className="w-7 h-7 mx-auto mb-2 text-[#C9C3B6]" />
          <p className="text-[13px] text-muted-foreground">No components registered</p>
        </div>
      ) : (
        components.map(comp => (
          <Card key={comp.id} className="hover:border-primary/30 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-secondary text-xs">{componentTypeLabels[comp.component_type] || comp.component_type}</Badge>
                    <span className="font-mono text-xs text-muted-foreground">{comp.comp_number}</span>
                  </div>
                  <p className="font-semibold">{comp.description}</p>
                </div>
                <Badge className={comp.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>{comp.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground mt-2">
                {comp.serial_number && <span>S/N: <span className="font-mono">{comp.serial_number}</span></span>}
                {comp.manufacturer && <span>{comp.manufacturer}</span>}
                {comp.swl && <span className="text-[#FFD108] font-semibold">SWL: {comp.swl} {comp.swl_unit}</span>}
                {comp.certificate_count > 0 && <span><Award className="w-3 h-3 inline mr-0.5" />{comp.certificate_count} cert{comp.certificate_count !== 1 ? 's' : ''}</span>}
              </div>
            </CardContent>
          </Card>
        ))
      )}
      </div>

      {/* Link auxiliary asset drawer */}
      <SlideDrawer
        open={showLinkAux}
        onOpenChange={setShowLinkAux}
        title="Link auxiliary asset"
        size="sm"
      >
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Showing auxiliary assets in your tenant that aren't already linked to another parent.
            Linking one here sets its parent to <span className="text-foreground font-mono">{assetName}</span>.
          </p>
          <Input
            placeholder="Search by unit #, serial, make…"
            value={auxPicker.search}
            onChange={(e) => setAuxPicker(p => ({ ...p, search: e.target.value }))}
            data-testid="aux-picker-search"
            autoFocus
          />
          {auxPicker.loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : filteredAuxOptions.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border rounded-sm">
              {auxPicker.options.length === 0
                ? 'No auxiliary assets exist in your tenant yet. Mark an asset as Auxiliary when creating it.'
                : 'No matches for that search.'}
            </p>
          ) : (
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
              {filteredAuxOptions.map(opt => {
                const reassigning = !!opt._currentParent;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      if (reassigning) {
                        const currentLabel = opt._currentParent.unit_number || opt._currentParent.name;
                        confirm({
                          title: 'Move this auxiliary asset?',
                          description: `"${opt.unit_number || opt.name}" is currently linked to ${currentLabel}. Move it to ${assetName} instead?`,
                          confirmLabel: 'Move it',
                          testId: 'confirm-move-auxiliary',
                          onConfirm: async () => { await linkAuxiliary(opt); },
                        });
                        return;
                      }
                      linkAuxiliary(opt);
                    }}
                    disabled={auxPicker.saving === opt.id}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left rounded-sm border transition-colors disabled:opacity-50 ${
                      reassigning
                        ? 'border-amber-500/30 bg-amber-500/[0.04] hover:border-amber-500/50'
                        : 'border-border bg-card hover:border-primary/40 hover:bg-secondary/40'
                    }`}
                    data-testid={`aux-picker-row-${opt.id}`}
                  >
                    <Package className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{opt.unit_number || opt.name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono truncate">
                        {opt.serial_number}{opt.make && ` · ${opt.make} ${opt.model || ''}`}
                      </div>
                      {reassigning && (
                        <div className="text-[10px] text-amber-300 mt-0.5 font-mono">
                          ↳ currently linked to {opt._currentParent.unit_number || opt._currentParent.name}
                        </div>
                      )}
                    </div>
                    {auxPicker.saving === opt.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : reassigning ? (
                      <span className="text-[10px] uppercase text-amber-300 font-mono whitespace-nowrap">Reassign</span>
                    ) : (
                      <Plus className="w-3.5 h-3.5 text-muted-foreground" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </SlideDrawer>

      <ConfirmDeleteDialog
        open={!!unlinkTarget}
        onOpenChange={(o) => { if (!o) setUnlinkTarget(null); }}
        title="Unlink auxiliary asset?"
        description={
          unlinkTarget
            ? `"${unlinkTarget.unit_number || unlinkTarget.name}" will no longer be tied to ${assetName}. The auxiliary asset itself remains intact.`
            : ''
        }
        confirmLabel="Unlink"
        loading={unlinking}
        onConfirm={unlinkAuxiliary}
        testId="confirm-unlink-aux"
      />
      <ConfirmDeleteDialog {...dialogProps} />
    </CardStack>
  );
};

// Definition-list field for the Asset Detail sheet (warm light palette).
const SheetField = ({ label, children, mono, className = '', span = '' }) => (
  <div className={span}>
    <div className="as-flabel">{label}</div>
    <div className={`as-fval mt-[3px] ${mono ? 'as-mono text-[12.5px]' : ''} ${className}`}>{children}</div>
  </div>
);

// App-wide button shape — the sheet header Inspect/Edit/Delete trio is the
// gold standard. Fill varies by role; shape/size/typography stay identical.
const BTN_BASE = 'inline-flex items-center justify-center h-8 px-3 rounded-[7px] text-[12px] font-semibold transition-colors disabled:opacity-45 disabled:cursor-not-allowed';
const BTN_OUTLINE = `${BTN_BASE} bg-white border border-[#C9C3B6] text-[#1A1A1A] hover:bg-[#FBFAF7]`;
const BTN_VOLT = `${BTN_BASE} uppercase tracking-[0.04em] font-bold bg-[#FFD108] border border-[#E0B800] text-black hover:bg-[#E5B93D]`;
const BTN_DANGER = `${BTN_BASE} bg-white border border-[#F5C0B8] text-[#A32014] hover:bg-[#FFEDEA]`;
const BTN_VOLT_OUTLINE = `${BTN_BASE} bg-[#FFF6E0] border border-[#EFD9A3] text-[#8A6D00] hover:bg-[#FFF0CC]`;

const HISTORY_TYPES = [
  { key: 'all', label: 'All' },
  { key: 'work_order', label: 'Work Orders' },
  { key: 'inspection', label: 'Inspections' },
  { key: 'defect', label: 'Defects' },
  { key: 'service', label: 'Services' },
  { key: 'certificate', label: 'Certificates' },
  { key: 'location', label: 'Location' },
];
const HISTORY_META = {
  work_order: { icon: ClipboardCheck, color: '#C9A400', label: 'Work Order' },
  inspection: { icon: Eye, color: '#2E7D5B', label: 'Inspection' },
  defect: { icon: AlertTriangle, color: '#C0492B', label: 'Defect' },
  service: { icon: History, color: '#3A6EA5', label: 'Service' },
  certificate: { icon: Award, color: '#7A5B00', label: 'Certificate' },
  location: { icon: MapPin, color: '#8A857A', label: 'Location' },
};

const AssetHistoryTab = ({ assetId }) => {
  const { formatDateTime } = useSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try { const r = await assetsApi.getHistory(assetId); if (active) setEvents(r.data || []); }
      catch { toast.error('Failed to load history'); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [assetId]);

  const openPdf = async (ev, mode) => {
    setBusyId(ev.type + ev.id + mode);
    try {
      const res = await assetsApi.downloadFile(ev.pdf_url);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (mode === 'download') {
        const a = document.createElement('a');
        a.href = url; a.download = ev.pdf_name || 'document.pdf';
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, '_blank', 'noopener');
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not load PDF'); }
    finally { setBusyId(null); }
  };

  const shown = filter === 'all' ? events : events.filter((e) => e.type === filter);

  return (
    <div className="as-card p-4" data-testid="asset-history-tab">
      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        {HISTORY_TYPES.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            data-testid={`history-filter-${t.key}`}
            className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${filter === t.key ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white text-[#59554D] border-[#E2DED4] hover:border-[#C9A400]'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-[#C9A400] animate-spin" /></div>
      ) : shown.length === 0 ? (
        <p className="text-[12.5px] text-[#59554D] text-center py-10">No history recorded</p>
      ) : (
        <div className="relative pl-6">
          <div className="absolute left-[9px] top-1 bottom-1 w-px bg-[#E2DED4]" />
          {shown.map((ev) => {
            const meta = HISTORY_META[ev.type] || HISTORY_META.location;
            const Icon = meta.icon;
            return (
              <div key={ev.type + ev.id} className="relative mb-4 last:mb-0" data-testid={`history-event-${ev.id}`}>
                <div className="absolute -left-6 top-0.5 flex items-center justify-center w-[19px] h-[19px] rounded-full bg-white border-2" style={{ borderColor: meta.color }}>
                  <Icon className="w-2.5 h-2.5" style={{ color: meta.color }} />
                </div>
                <div className="rounded-lg bg-[#FBFAF7] border border-[#E2DED4] p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                        {ev.status && <StatusPill value={ev.status} size="sm" />}
                      </div>
                      <button
                        onClick={() => ev.link && navigate(ev.link)}
                        disabled={!ev.link}
                        className={`block text-left text-[13px] font-semibold text-[#1A1A1A] mt-1 ${ev.link ? 'hover:text-[#C9A400] cursor-pointer' : 'cursor-default'}`}
                      >
                        {ev.title}
                      </button>
                      <p className="as-mono text-[11px] text-[#8A857A] mt-0.5">
                        {formatDateTime(ev.timestamp)}{ev.subtitle ? ` · ${ev.subtitle}` : ''}
                      </p>
                    </div>
                    {ev.pdf_url && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => openPdf(ev, 'view')}
                          data-testid={`history-pdf-view-${ev.id}`}
                          title="View PDF"
                          className="h-8 px-2.5 rounded-md border border-[#E2DED4] bg-white text-[11.5px] font-semibold text-[#1A1A1A] hover:border-[#C9A400] flex items-center gap-1"
                        >
                          {busyId === ev.type + ev.id + 'view' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} View
                        </button>
                        <button
                          onClick={() => openPdf(ev, 'download')}
                          data-testid={`history-pdf-download-${ev.id}`}
                          title="Download PDF"
                          className="h-8 w-8 rounded-md border border-[#E2DED4] bg-white text-[#1A1A1A] hover:border-[#C9A400] flex items-center justify-center"
                        >
                          {busyId === ev.type + ev.id + 'download' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const AssetDetailView = ({ asset, inspections, statuses, onEdit, onDelete, onClose, onUpdated, token }) => {
  const [editingHours, setEditingHours] = useState(false);
  const [hoursVal, setHoursVal] = useState('');
  const saveHours = async () => {
    const v = Number(hoursVal);
    if (Number.isNaN(v) || v < 0) { toast.error('Enter a valid meter reading'); return; }
    try {
      await assetsApi.update(asset.id, { hours: v });
      toast.success('Meter reading updated');
      setEditingHours(false);
      onUpdated && onUpdated();
    } catch (e) {
      toast.error('Could not update meter');
    }
  };
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { formatDateTime } = useSettings();
  const [activeTab, setActiveTab] = useState('details');
  const [certPrefill, setCertPrefill] = useState(null);
  const [qOpen, setQOpen] = useState(false);
  const [qReason, setQReason] = useState('');
  const [qBusy, setQBusy] = useState(false);
  const quarantined = asset.quarantine?.active;
  const clearOverride = async () => {
    try {
      await assetsApi.bulkReadiness({ asset_ids: [asset.id], status: 'auto' });
      toast.success('Override cleared');
      onUpdated && onUpdated();
    } catch (e) { toast.error('Could not clear override'); }
  };
  const doQuarantine = async () => {
    if (!qReason.trim()) { toast.error('Enter a reason'); return; }
    setQBusy(true);
    try { await assetsApi.quarantine(asset.id, qReason.trim()); toast.success('Asset quarantined'); setQOpen(false); setQReason(''); onUpdated && onUpdated(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not quarantine'); }
    finally { setQBusy(false); }
  };
  const doRelease = async () => {
    setQBusy(true);
    try { await assetsApi.releaseQuarantine(asset.id); toast.success('Quarantine released'); onUpdated && onUpdated(); }
    catch { toast.error('Could not release'); }
    finally { setQBusy(false); }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.download = `${asset.unit_number || asset.name}-qr.png`;
    link.href = `data:image/png;base64,${asset.qr_code}`;
    link.click();
  };

  // Public QR sticker PDF — backend builds an A4 sheet of stickers with
  // the volt-yellow "EQUIPR GUARDIAN" header bar, big unit number, QR
  // and scan-call-to-action. Operator can take this to a print shop.
  const [stickerCopies, setStickerCopies] = useState(8);
  const [downloadingSticker, setDownloadingSticker] = useState(false);
  const downloadStickerPdf = async () => {
    setDownloadingSticker(true);
    try {
      const res = await assetsApi.downloadQrStickerPdf(asset.id, stickerCopies);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-stickers-${asset.unit_number || asset.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${stickerCopies} sticker${stickerCopies === 1 ? '' : 's'}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to generate sticker PDF');
    } finally {
      setDownloadingSticker(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Title block */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[24px] font-extrabold tracking-[-0.015em] text-[#1A1A1A] leading-tight">{asset.unit_number || asset.name}</h2>
          {asset.name && asset.unit_number && (
            <p className="text-[13px] text-[#3D3A35] mt-0.5">{asset.name}</p>
          )}
          <div className="flex items-center gap-1.5 mt-1">
            <span className="as-mono text-[11.5px] text-[#59554D]">{asset.serial_number}</span>
            {asset.serial_number && (
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(asset.serial_number); toast.success('Serial copied'); }}
                className="inline-flex items-center justify-center w-5 h-5 rounded text-[#59554D] hover:bg-[#F0EDE7] transition-colors"
                title="Copy serial number"
                data-testid="copy-serial-btn"
              >
                <Copy className="w-3 h-3" />
              </button>
            )}
          </div>
          {asset.external_id && asset.external_source === 'allocate' && (
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(asset.external_id);
                toast.success('Allocate ID copied');
              }}
              className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-[#EFD9A3] bg-[#FFF6E0] text-[#8A6D00] hover:bg-[#FFF0CC] transition-colors font-mono"
              title={`Click to copy · Synced from EQUIPR Allocate`}
              data-testid="allocate-pill"
            >
              <Link2 className="w-3 h-3" />
              Allocate · {asset.external_id}
            </button>
          )}
        </div>
        <StatusPill value={asset.status} size="sm" className="flex-shrink-0" />
      </div>

      {/* Yard readiness */}
      <div className="rounded-lg border border-border bg-card p-3" data-testid="drawer-readiness">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ReadinessBadge readiness={asset.readiness} />
            <GearChip gear={asset.readiness?.gear} />
            <span className="text-xs text-muted-foreground">Yard readiness</span>
          </div>
          {isAdmin() && (quarantined ? (
            <Button size="sm" variant="outline" onClick={doRelease} disabled={qBusy} data-testid="release-quarantine-btn">Release quarantine</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setQOpen(o => !o)} data-testid="quarantine-btn">Quarantine</Button>
          ))}
        </div>
        {asset.readiness?.reasons?.length > 0 && (
          <ul className="mt-2 text-xs text-muted-foreground list-disc pl-4 space-y-0.5" data-testid="drawer-readiness-reasons">
            {asset.readiness.reasons.slice(0, 8).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}
        {asset.readiness?.override && (
          <div className="mt-2 text-xs rounded border px-2 py-1 flex items-center justify-between gap-2" style={{ background: '#F4F2EE', borderColor: '#DEDAD1', color: '#3D3A35' }} data-testid="readiness-override-banner">
            <span>Manual override active. Calculated status: {READINESS_META[asset.readiness.computed_status]?.label || asset.readiness.computed_status}</span>
            {isAdmin() && (
              <button type="button" className="underline font-semibold" onClick={clearOverride} data-testid="clear-readiness-override-btn">Clear override</button>
            )}
          </div>
        )}
        {quarantined && (
          <div className="mt-2 text-xs rounded border px-2 py-1" style={{ background: '#FEF2F2', borderColor: '#F6B4AC', color: '#991B1B' }} data-testid="quarantine-banner">
            Quarantined: {asset.quarantine.reason} · by {asset.quarantine.by_name}
          </div>
        )}
        {asset.readiness?.gear?.worst && (
          <div className="mt-2 text-xs rounded border px-2 py-1 flex items-start gap-1.5" style={{ background: '#FFFBEB', borderColor: '#F2D97A', color: '#92600A' }} data-testid="drawer-gear-warning">
            <Wrench className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Linked gear needs attention: {asset.readiness.gear.items.map((i) => i.name).join(', ')}</span>
          </div>
        )}
        {qOpen && !quarantined && (
          <div className="mt-2 flex items-center gap-2" data-testid="quarantine-form">
            <Input value={qReason} onChange={(e) => setQReason(e.target.value)} placeholder="Reason (logged)" className="h-8" data-testid="quarantine-reason" />
            <Button size="sm" onClick={doQuarantine} disabled={qBusy} className="btn-industrial" data-testid="quarantine-confirm">Confirm</Button>
          </div>
        )}
      </div>

      {/* Action row */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(`/inspections/new?asset=${asset.id}`)}
          className="inline-flex items-center h-8 px-3 rounded-[7px] text-[12px] font-semibold uppercase tracking-[0.04em] bg-white border border-[#C9C3B6] text-[#1A1A1A] hover:bg-[#FBFAF7] transition-colors"
          data-testid="drawer-inspect-btn"
        >
          <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />
          Inspect
        </button>
        {isAdmin() && (
          <>
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center h-8 px-3 rounded-[7px] text-[12px] font-bold uppercase tracking-[0.04em] bg-[#FFD108] border border-[#E0B800] text-black hover:bg-[#E5B93D] transition-colors"
              data-testid="drawer-edit-btn"
            >
              <Edit className="w-3.5 h-3.5 mr-1.5" />
              Edit
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="ml-auto inline-flex items-center h-8 px-3 rounded-[7px] text-[12px] font-semibold uppercase tracking-[0.04em] bg-[#D93526] border border-[#B8291C] text-white hover:bg-[#c22d20] transition-colors"
              data-testid="drawer-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Delete
            </button>
          </>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="as-tabbar w-full h-auto">
          <TabsTrigger value="details" className="as-tab">Details</TabsTrigger>
          <TabsTrigger value="compliance" className="as-tab" data-testid="compliance-tab">Compliance</TabsTrigger>
          <TabsTrigger value="inspections" className="as-tab">Inspections</TabsTrigger>
          <TabsTrigger value="certificates" className="as-tab" data-testid="certificates-tab">Certificates</TabsTrigger>
          <TabsTrigger value="history" className="as-tab" data-testid="history-tab">History</TabsTrigger>
          <TabsTrigger value="components" className="as-tab" data-testid="components-tab">Components</TabsTrigger>
          <TabsTrigger value="telemetry" className="as-tab" data-testid="telemetry-tab">Telemetry</TabsTrigger>
          <TabsTrigger value="qrcode" className="as-tab">QR Code</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="flex flex-col gap-4">
          <div className="as-card p-4">
            <div className="as-section mb-3">Asset Information</div>
            <div className="grid grid-cols-3 gap-x-5 gap-y-3.5">
              <SheetField label="Category"><span className="capitalize">{(asset.category || '').replace(/_/g, ' ')}</span></SheetField>
              <SheetField label="Make">{asset.make}</SheetField>
              <SheetField label="Model">{asset.model}</SheetField>
              <SheetField label="Year" mono>{asset.year}</SheetField>
              <SheetField label="Primary meter" mono>
                {editingHours ? (
                  <span className="inline-flex items-center gap-1.5">
                    <input
                      type="number"
                      min="0"
                      autoFocus
                      value={hoursVal}
                      onChange={(e) => setHoursVal(e.target.value)}
                      className="w-24 px-2 py-1 rounded border border-[#D4CFC2] text-[13px]"
                      data-testid="asset-quick-hours-input"
                    />
                    <button onClick={saveHours} className="text-[12px] font-semibold text-[#B45309] hover:underline" data-testid="asset-quick-hours-save">Save</button>
                    <button onClick={() => setEditingHours(false)} className="text-[12px] text-[#8A857A] hover:underline">Cancel</button>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    {formatMeter(asset.hours, asset.hours_unit || 'hours')}
                    <button
                      onClick={() => { setHoursVal(String(asset.hours ?? 0)); setEditingHours(true); }}
                      className="text-[11px] font-semibold text-[#B45309] hover:underline"
                      data-testid="asset-update-hours-btn"
                    >
                      Update
                    </button>
                  </span>
                )}
              </SheetField>
              <SheetField label="Secondary meter">
                {asset.secondary_meter != null
                  ? <span className="as-mono text-[12.5px]">{formatMeter(asset.secondary_meter, asset.secondary_meter_unit || 'hours')}</span>
                  : <span className="italic text-[#8A857A]">Not set</span>}
              </SheetField>
              <SheetField label="VIN" mono>{asset.vin || <span className="italic text-[#8A857A]" data-testid="asset-vin-empty">Not set</span>}</SheetField>
              <SheetField label="Registration" mono>{asset.registration || <span className="italic text-[#8A857A]" data-testid="asset-registration-empty">Not set</span>}</SheetField>
              {asset.depot_name && (
                <SheetField label="Depot">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-[#C9A400]" />
                    {asset.depot_name}
                  </span>
                </SheetField>
              )}
              {asset.notes && (
                <SheetField label="Notes" span="col-span-3">{asset.notes}</SheetField>
              )}
            </div>
          </div>

          {/* Asset Images — 5-up grid */}
          <div className="as-card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="as-section"><ImageIcon className="w-3.5 h-3.5 text-[#C9A400]" />Asset Images</div>
              <span className="text-[10px] uppercase tracking-[0.09em] text-[#8A857A] font-semibold">Max 5</span>
            </div>
            <div className="grid grid-cols-5 gap-2.5">
              {(asset.images || []).map((img, index) => (
                <div key={index} className="rounded-lg overflow-hidden bg-[#F2F0EA] border border-[#E6E2D9]" style={{ aspectRatio: '4/3' }}>
                  <img
                    src={getImageSrc(img, token)}
                    alt={`Asset ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => window.open(getImageSrc(img, token), '_blank')}
                  />
                </div>
              ))}
              {isAdmin() && (asset.images?.length || 0) < 5 && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="rounded-lg border-2 border-dashed border-[#C9C3B6] flex flex-col items-center justify-center text-[#8A857A] hover:border-[#C9A400] hover:text-[#C9A400] transition-colors"
                  style={{ aspectRatio: '4/3' }}
                  data-testid="add-photo-tile"
                >
                  <Camera className="w-4 h-4 mb-0.5" />
                  <span className="text-[10px]">Add photo</span>
                </button>
              )}
            </div>
          </div>

          {/* Quick Actions — single row */}
          <div className="as-card p-4">
            <div className="as-section mb-3">Quick Actions</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { icon: ClipboardCheck, label: 'Perform Inspection', to: `/inspections/new?asset=${asset.id}` },
                { icon: AlertTriangle, label: 'Report Defect', to: `/defects/new?asset=${asset.id}` },
                { icon: History, label: 'Create Work Order', to: `/work-orders/new?asset=${asset.id}` },
              ].map(({ icon: Icon, label, to }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => navigate(to)}
                  className="flex items-center justify-center gap-1.5 h-[38px] rounded-lg bg-[#FBFAF7] border border-[#E2DED4] text-[12.5px] text-[#1A1A1A] hover:bg-[#F2F0EA] transition-colors px-2"
                >
                  <Icon className="w-[15px] h-[15px] text-[#59554D]" />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Phase 51: linked OEM error-code manuals. Operators scanning
              this asset's QR can look up fault codes in the field. The card
              manages its own link state — no parent refresh required. */}
          <AssetErrorCodeLibrariesCard asset={asset} />

          {/* Phase 52: per-asset whitelist of Inspection templates exposed
              to anonymous Public-QR scanners. */}
          <AssetPublicInspectionsCard asset={asset} />
        </TabsContent>

        <TabsContent value="compliance">
          <AssetComplianceCard
            assetId={asset.id}
            onNavigateToCertificates={(slot) => {
              setCertPrefill(slot || null);
              setActiveTab('certificates');
            }}
          />
        </TabsContent>

        <TabsContent value="inspections">
          <div className="as-card p-4">
            <div className="as-section mb-3">Inspection History</div>
            {inspections.length === 0 ? (
              <p className="text-[12.5px] text-[#59554D] text-center py-8">No inspections recorded</p>
            ) : (
              <div className="flex flex-col gap-3">
                {inspections.map(inspection => (
                  <div
                    key={inspection.id}
                    className="flex items-center justify-between rounded-lg bg-[#FBFAF7] border border-[#E2DED4] hover:border-[#C9A400] cursor-pointer transition-colors"
                    style={{ padding: '11px 13px' }}
                    onClick={() => navigate(`/inspections/${inspection.id}`)}
                  >
                    <div>
                      <p className="text-[12.5px] font-semibold text-[#1A1A1A]">{inspection.inspector_name}</p>
                      <p className="as-mono text-[11px] text-[#8A857A] mt-0.5">{formatDateTime(inspection.created_at)}</p>
                    </div>
                    <StatusPill value={inspection.status} size="sm" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="certificates">
          <CertificatesTab assetId={asset.id} prefill={certPrefill} onPrefillConsumed={() => setCertPrefill(null)} />
        </TabsContent>

        <TabsContent value="history">
          <AssetHistoryTab assetId={asset.id} />
        </TabsContent>

        <TabsContent value="components">
          <ComponentsTab assetId={asset.id} assetName={asset.unit_number || asset.name} />
        </TabsContent>

        <TabsContent value="qrcode">
          <CardStack>
            {/* Internal QR Code */}
            <div className="as-card p-4 flex flex-col items-center gap-3">
              <p className="as-section self-start">Internal QR Code</p>
              <div className="bg-white border border-[#E2DED4] rounded-[10px] p-3.5" style={{ width: 172, height: 172 }}>
                <img
                  src={`data:image/png;base64,${asset.qr_code}`}
                  alt="Internal QR Code"
                  className="w-full h-full"
                />
              </div>
              <p className="text-[#59554D] text-center text-[11.5px] max-w-sm leading-relaxed">
                For logged-in Guardian users. Scanning redirects to the asset detail page within the app.
              </p>
              <button
                type="button"
                onClick={downloadQRCode}
                className="inline-flex items-center h-8 px-3 rounded-[7px] text-[12px] font-bold uppercase tracking-[0.04em] bg-[#FFD108] border border-[#E0B800] text-black hover:bg-[#E5B93D] transition-colors"
                data-testid="drawer-download-qr-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Download Internal QR
              </button>
            </div>

            {/* Public QR Code */}
            {asset.public_qr_token && (
              <div className="as-card p-4 flex flex-col items-center gap-3">
                <p className="as-section self-start">Public QR Code</p>
                <div className="bg-white border border-[#E2DED4] rounded-[10px] p-3.5" style={{ width: 172, height: 172 }}>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                      `${window.location.origin}/public/asset/${asset.public_qr_token}`
                    )}`}
                    alt="Public QR Code"
                    className="w-full h-full"
                    data-testid="public-qr-image"
                  />
                </div>
                <p className="text-[#59554D] text-center text-[11.5px] max-w-sm leading-relaxed">
                  For external operators & visitors. <strong className="text-[#1A1A1A]">No login required.</strong> Shows public asset info and allows anonymous issue reporting.
                </p>
                <code className="as-mono text-[12px] bg-[#FFF6E0] border border-[#EFD9A3] px-2 py-1 rounded text-[#8A6D00]">
                  {asset.public_qr_token}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    const url = `${window.location.origin}/public/asset/${asset.public_qr_token}`;
                    navigator.clipboard.writeText(url);
                    toast.success('Public URL copied');
                  }}
                  className="inline-flex items-center h-8 px-3 rounded-[7px] text-[12px] font-semibold uppercase tracking-[0.04em] bg-white border border-[#C9C3B6] text-[#1A1A1A] hover:bg-[#FBFAF7] transition-colors"
                  data-testid="copy-public-url-btn"
                >
                  <LinkIcon className="w-3.5 h-3.5 mr-1.5" />
                  Copy Public URL
                </button>

                {/* Phase 51b: Print-ready sticker PDF (A4 grid). */}
                <div className="w-full max-w-sm mt-2 pt-4 border-t border-[#F0EDE6] space-y-3">
                  <p className="as-section justify-center">Print as Sticker</p>
                  <div className="flex items-center gap-2 justify-center">
                    <Label htmlFor="sticker-copies-input" className="as-flabel whitespace-nowrap">
                      Copies
                    </Label>
                    <Input
                      id="sticker-copies-input"
                      type="number"
                      min={1}
                      max={64}
                      value={stickerCopies}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setStickerCopies(Number.isNaN(v) ? 1 : Math.max(1, Math.min(64, v)));
                      }}
                      className="h-9 w-14 text-sm as-mono text-center"
                      data-testid="sticker-copies-input"
                    />
                    <Button
                      size="sm"
                      onClick={downloadStickerPdf}
                      disabled={downloadingSticker}
                      className="flex-1 btn-volt"
                      data-testid="download-sticker-pdf-btn"
                    >
                      {downloadingSticker ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download Sticker PDF
                    </Button>
                  </div>
                  <p className="text-[11px] text-[#59554D] text-center leading-relaxed">
                    A4 sheet, 8 stickers per page (90 × 60 mm each).
                    Branded with EQUIPR GUARDIAN — take to any print shop for adhesive cab/control-box decals.
                  </p>
                </div>
              </div>
            )}
          </CardStack>
        </TabsContent>

        <TabsContent value="telemetry" className="space-y-4">
          <TelemetryCard assetId={asset.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export const AssetsListPage = () => {
  const { confirm, dialogProps } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [depotFilter, setDepotFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  // Density: 'compact' | 'cosy' | 'comfortable'. Persisted to localStorage so
  // it survives reloads — admins running a 100-asset fleet will pick 'compact'
  // once and never touch it again.
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('assets_density') || 'cosy'; } catch { return 'cosy'; }
  });
  // Sort: column + direction. Initial value read from URL so deep links
  // (e.g. /assets?sort=hours&dir=desc) work for shared compliance reports.
  const [sortKey, setSortKey] = useState(() => searchParams.get('sort') || '');
  const [sortDir, setSortDir] = useState(() => searchParams.get('dir') === 'desc' ? 'desc' : 'asc');
  // Highlighted row index for keyboard navigation (↑↓ + Enter).
  const [highlightedIdx, setHighlightedIdx] = useState(-1);

  // ── Column visibility (List view) ────────────────────────────────────────
  // Power users on 400+ asset fleets want to declutter the table. We keep the
  // full schema here and let users tick / untick the optional columns.
  // Persisted per-browser in localStorage so the choice follows them across
  // sessions without needing a backend round-trip.
  const ASSET_COLUMNS = useMemo(() => ([
    { key: 'unit_number',   label: 'Unit #',        required: true  },
    { key: 'serial_number', label: 'Serial Number', required: false },
    { key: 'category',      label: 'Category',      required: false },
    { key: 'make_model',    label: 'Make / Model',  required: false },
    { key: 'year',          label: 'Year',          required: false },
    { key: 'hours',         label: 'Meter',         required: false },
    { key: 'depot',         label: 'Depot',         required: false },
    { key: 'status',        label: 'Status',        required: false },
  ]), []);
  const DEFAULT_VISIBLE_COLUMNS = useMemo(
    () => new Set(ASSET_COLUMNS.map(c => c.key)),
    [ASSET_COLUMNS],
  );
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem('assets_visible_columns');
      if (!raw) return DEFAULT_VISIBLE_COLUMNS;
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr) || !arr.length) return DEFAULT_VISIBLE_COLUMNS;
      // Force-include required columns even if a stale preference dropped them.
      const set = new Set(arr);
      ASSET_COLUMNS.forEach(c => { if (c.required) set.add(c.key); });
      return set;
    } catch {
      return DEFAULT_VISIBLE_COLUMNS;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('assets_visible_columns', JSON.stringify([...visibleColumns]));
    } catch { /* private-mode */ }
  }, [visibleColumns]);

  // Fix 5: row selection for bulk actions.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const [bulkDialog, setBulkDialog] = useState(null);
  const onBulkDone = () => { setBulkDialog(null); clearSelection(); loadAssets(true); };

  // Server-side pagination — 25 per page with an explicit "Load more" button.
  // Filtering + sorting now happen in the backend query so this scales to
  // very large fleets without shipping the whole table to the browser.
  const PAGE_SIZE = 25;
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [facetCategories, setFacetCategories] = useState([]);
  const [fleetSummary, setFleetSummary] = useState({ fleet_total: 0, active: 0, out_of_service: 0 });
  
  // Drawer states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(null); // 'add' | 'edit' | 'view'
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedAssetInspections, setSelectedAssetInspections] = useState([]);
  const [loadingAsset, setLoadingAsset] = useState(false);
  
  // Dynamic lookups
  const { data: categories, loading: loadingCategories } = useLookup('asset_category');
  const { data: statuses, loading: loadingStatuses } = useLookup('asset_status');
  const { data: makes } = useEquipmentMakes();
  const { data: models } = useEquipmentModels();
  
  const { isAdmin, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadDepots();
  }, []);

  // Handle view query parameter (from QR scan redirect)
  useEffect(() => {
    const viewAssetId = searchParams.get('view');
    if (viewAssetId && assets.length > 0) {
      openViewDrawer(viewAssetId);
      // Clear the query parameter
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, assets]);

  const [readinessFilter, setReadinessFilter] = useState('all');

  const buildAssetParams = (nextOffset) => {
    const params = { limit: PAGE_SIZE, offset: nextOffset };
    if (search.trim()) params.search = search.trim();
    if (statusFilter !== 'all') params.status = statusFilter;
    if (categoryFilter !== 'all') params.category = categoryFilter;
    if (depotFilter !== 'all') params.depot_id = depotFilter;
    if (classFilter !== 'all') params.asset_class = classFilter;
    if (readinessFilter !== 'all') params.readiness = readinessFilter;
    if (sortKey) { params.sort = sortKey; params.sort_dir = sortDir; }
    return params;
  };

  const loadAssets = async (reset = true) => {
    const nextOffset = reset ? 0 : offset + PAGE_SIZE;
    reset ? setLoading(true) : setLoadingMore(true);
    try {
      const response = await assetsApi.getAll(buildAssetParams(nextOffset));
      const data = response.data;
      setTotal(data.total);
      setOffset(nextOffset);
      if (data.facets?.categories) setFacetCategories(data.facets.categories);
      if (data.summary) setFleetSummary(data.summary);
      setAssets((prev) => (reset ? data.items : [...prev, ...data.items]));
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadDepots = async () => {
    try {
      const response = await depotsApi.list();
      setDepots(response.data);
    } catch (error) {
      console.error('Failed to load depots:', error);
    }
  };

  const loadAssetDetails = async (id) => {
    setLoadingAsset(true);
    setSelectedAssetInspections([]);
    try {
      // Load the asset first — if THIS fails we can't render the drawer.
      const assetRes = await assetsApi.getById(id);
      setSelectedAsset(assetRes.data);
    } catch (error) {
      // Surface the real backend detail so a Pydantic validation failure
      // on one weird record doesn't show as a vague "Failed to load".
      const detail = error?.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? `Cannot load asset: ${detail}`
          : 'Failed to load asset details'
      );
      closeDrawer();
      setLoadingAsset(false);
      return;
    }
    // Inspections list is a secondary signal — don't block the drawer
    // if it fails. Log + show a small inline warning instead.
    try {
      const inspectionsRes = await inspectionsApi.getAll({ asset_id: id });
      setSelectedAssetInspections(inspectionsRes.data);
    } catch (error) {
      console.warn('Inspections list failed:', error);
      // Leave selectedAssetInspections as [] — UI just shows "no inspections".
    } finally {
      setLoadingAsset(false);
    }
  };

  // Helper to get category/status label. Falls back to a Title-Cased version of
  // the value itself so arbitrary Allocate-imported categories display nicely
  // (e.g. "60_65t_all_terrain_crane" → "60 65t All Terrain Crane").
  const titleCase = (v) =>
    (v || '')
      .split('_')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat?.name || titleCase(value) || 'Unknown';
  };

  const getStatusLabel = (value) => {
    const stat = statuses.find(s => s.value === value);
    return stat?.name || titleCase(value) || 'Unknown';
  };

  const exportCell = (a, key) => ({
    unit_number: a.unit_number || a.name,
    serial_number: a.serial_number,
    category: getCategoryLabel(a.category),
    make_model: `${a.make || ''} ${a.model || ''}`.trim(),
    year: a.year,
    hours: a.hours,
    depot: a.depot_name,
    status: getStatusLabel(a.status),
  }[key]);
  const exportSelection = () => {
    const rows = assets.filter(a => selectedIds.has(a.id));
    exportAssetsCsv(rows, ASSET_COLUMNS.filter(c => visibleColumns.has(c.key)), exportCell);
    toast.success(`Exported ${rows.length} asset(s)`);
  };

  // Categories for the filter dropdown — sourced from the backend facet
  // (distinct categories across the whole tenant, independent of the current
  // page/filters) merged with any built-in lookup categories.
  const tenantCategories = useMemo(() => {
    const seen = new Set();
    for (const v of facetCategories) if (v) seen.add(v);
    for (const c of categories) if (c.value) seen.add(c.value);
    return Array.from(seen).sort();
  }, [facetCategories, categories]);

  // Server already applies search / status / category / depot / class filters
  // and sorting, so the rendered list is simply the accumulated pages.
  const filteredAssets = assets;

  // Persist density preference (best-effort; safe if storage is unavailable).
  useEffect(() => {
    try { localStorage.setItem('assets_density', density); } catch { /* private-mode */ }
  }, [density]);

  // Persist sort to URL so deep links (e.g. /assets?sort=hours&dir=desc) work.
  // Uses `replace: true` to avoid spamming history when the user toggles dir.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (sortKey) {
      next.set('sort', sortKey);
      next.set('dir', sortDir);
    } else {
      next.delete('sort');
      next.delete('dir');
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey, sortDir]);

  // Toggle: same column → flip direction; new column → asc.
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Keyboard navigation on the list view. Ignored while typing in an input or
  // when the drawer is open (drawer has its own focus trap).
  useEffect(() => {
    if (viewMode !== 'list' || drawerOpen) return;
    const onKey = (e) => {
      const target = e.target;
      const isTyping = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (isTyping) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIdx((i) => Math.min((i < 0 ? -1 : i) + 1, filteredAssets.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && highlightedIdx >= 0 && highlightedIdx < filteredAssets.length) {
        e.preventDefault();
        openViewDrawer(filteredAssets[highlightedIdx].id);
      } else if (e.key === 'Escape') {
        setHighlightedIdx(-1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, drawerOpen, filteredAssets, highlightedIdx]);

  // Reset highlight when filters/sort change to avoid pointing at a stale row.
  useEffect(() => {
    setHighlightedIdx(-1);
  }, [search, statusFilter, categoryFilter, depotFilter, classFilter, sortKey, sortDir]);

  // Re-query the server (page 0) whenever any filter or sort changes. Debounced
  // so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => loadAssets(true), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, categoryFilter, depotFilter, classFilter, readinessFilter, sortKey, sortDir]);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setClassFilter('all');
    setDepotFilter('all');
    setReadinessFilter('all');
  };

  const hasActiveFilters = search !== '' || statusFilter !== 'all' || categoryFilter !== 'all' || depotFilter !== 'all' || readinessFilter !== 'all';

  const openAddDrawer = () => {
    setSelectedAsset(null);
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const openEditDrawer = (asset) => {
    setSelectedAsset(asset);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const openViewDrawer = async (assetId) => {
    setDrawerMode('view');
    setDrawerOpen(true);
    await loadAssetDetails(assetId);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerMode(null);
    setSelectedAsset(null);
    setSelectedAssetInspections([]);
  };

  const handleFormSuccess = () => {
    closeDrawer();
    loadAssets();
  };

  const handleDelete = (id, e) => {
    e?.stopPropagation();
    const asset = assets.find(a => a.id === id);
    confirm({
      title: 'Delete this asset?',
      description: asset
        ? `"${asset.unit_number || asset.name || 'this asset'}" will be permanently removed.`
        : 'This asset will be permanently removed.',
      footnote: 'This cannot be undone. Linked inspections, defects and certificates may also be affected.',
      testId: 'confirm-delete-asset',
      onConfirm: async () => {
        try {
          await assetsApi.delete(id);
          toast.success('Asset deleted');
          closeDrawer();
          loadAssets();
        } catch (error) {
          toast.error('Failed to delete asset');
          throw error;
        }
      },
    });
  };

  const handleSwitchToEdit = () => {
    setDrawerMode('edit');
  };

  const getDrawerTitle = () => {
    switch (drawerMode) {
      case 'add': return 'Add New Asset';
      case 'edit': return 'Edit Asset';
      case 'view': return 'Asset Details';
      default: return '';
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="assets-page">
      {/* Free plan soft-limit banner — refreshes when asset count changes */}
      <PlanLimitBanner resource="assets" location="assets_page" refreshKey={assets.length} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Asset Registry
          </h1>
          <p className="text-muted-foreground">
            {fleetSummary.fleet_total} asset{fleetSummary.fleet_total === 1 ? '' : 's'} in fleet
            <span className="mx-1.5">·</span>
            <span className="text-foreground">{fleetSummary.active} active</span>
            {fleetSummary.out_of_service > 0 && (
              <>
                <span className="mx-1.5">·</span>
                <span style={{ color: '#B4380F' }}>{fleetSummary.out_of_service} out of service</span>
              </>
            )}
          </p>
        </div>
        {isAdmin() && (
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/assets/import')}
              variant="outline"
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button onClick={openAddDrawer} className="btn-industrial" data-testid="create-asset-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Asset
            </Button>
          </div>
        )}
      </div>

      <ReadinessSummaryBanner refreshKey={`${total}-${assets.length}`} />

      {/* Filters Card — sticky so it stays visible while scrolling long lists.
          top-16 matches the app header height; bg-background hides scrolled
          content showing through behind the translucent Card border. */}
      <Card className="overflow-hidden" data-testid="assets-registry-card">
        <div className="p-4 space-y-4 border-b border-border" data-testid="assets-filter-bar">
          {/* Top row - Search, Density, View Toggle */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative w-full sm:flex-1" style={{ minWidth: 280, maxWidth: 520 }}>
              <Search className="absolute top-1/2 -translate-y-1/2 text-[#59554D]" style={{ left: 12, width: 16, height: 16 }} />
              <input
                placeholder="Unit #, serial, make/model or IMEI"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-card text-foreground outline-none focus:border-[#1A1A1A]"
                style={{ height: 42, borderRadius: 9, border: '1.5px solid #1A1A1A', paddingLeft: 36, paddingRight: 74, fontSize: 14 }}
                data-testid="asset-search-input"
              />
              <span className="absolute top-1/2 -translate-y-1/2 font-mono pointer-events-none"
                style={{ right: 12, fontSize: 10.5, color: '#8A857A' }}
                data-testid="asset-search-count">
                {search || statusFilter !== 'all' || categoryFilter !== 'all' || depotFilter !== 'all' || classFilter !== 'all'
                  ? `${total} match${total === 1 ? '' : 'es'}`
                  : `${total} assets`}
              </span>
            </div>
            <div className="flex items-center gap-4">
              {/* Density toggle — only meaningful in list view, hidden in cards */}
              {viewMode === 'list' && (
                <div className="flex items-center gap-2" data-testid="density-toggle">
                  <span className="text-sm text-muted-foreground">Density:</span>
                  <div className="flex items-center gap-1 bg-secondary rounded-md p-0.5">
                    {[
                      { value: 'compact', icon: Rows3, label: 'Compact' },
                      { value: 'cosy', icon: Rows2, label: 'Cosy' },
                      { value: 'comfortable', icon: Square, label: 'Comfortable' },
                    ].map(opt => {
                      const Icon = opt.icon;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => setDensity(opt.value)}
                          title={opt.label}
                          aria-label={`${opt.label} density`}
                          data-testid={`density-${opt.value}-btn`}
                          className={`p-1.5 rounded transition-colors ${
                            density === opt.value
                              ? 'bg-[#FFD108] text-black'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {viewMode === 'list' && (
                <ColumnPicker
                  columns={ASSET_COLUMNS}
                  visible={visibleColumns}
                  onVisibleChange={setVisibleColumns}
                  onReset={() => setVisibleColumns(DEFAULT_VISIBLE_COLUMNS)}
                  storageKey="assets"
                  testId="asset-column-picker"
                />
              )}
            </div>
          </div>

          {/* Asset Class Toggle + Status & Category Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-1 bg-secondary rounded-lg p-1">
              {[
                { value: 'all', label: 'All' },
                { value: 'main', label: 'Main Assets' },
                { value: 'auxiliary', label: 'Auxiliary' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setClassFilter(opt.value)}
                  data-testid={`class-filter-${opt.value}`}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    classFilter === opt.value
                      ? 'bg-[#FFD108] text-black'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {statuses.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Category:</span>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-40" data-testid="category-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {tenantCategories.map(c => (
                    <SelectItem key={c} value={c}>{getCategoryLabel(c)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {depots.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Depot:</span>
                <Select value={depotFilter} onValueChange={setDepotFilter}>
                  <SelectTrigger className="w-44" data-testid="depot-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Depots</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {depots.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Readiness:</span>
              <Select value={readinessFilter} onValueChange={setReadinessFilter}>
                <SelectTrigger className="w-40" data-testid="readiness-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All readiness</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="amber">Attention</SelectItem>
                  <SelectItem value="red">Not ready</SelectItem>
                  <SelectItem value="grey">Not assessed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground hover:text-foreground"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}

            <div className="ml-auto text-sm text-muted-foreground">
              Showing {filteredAssets.length} of {total} assets
            </div>
          </div>
        </div>

      {/* Assets Display — bands inside the single registry card (Fix 4) */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredAssets.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Assets Found</h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters 
                ? 'Try adjusting your filters'
                : 'Get started by adding your first asset'}
            </p>
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : isAdmin() && (
              <Button onClick={openAddDrawer} data-testid="empty-create-asset-btn">
                <Plus className="w-5 h-5 mr-2" />
                Add First Asset
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'card' ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAssets.map(asset => (
            <Card 
              key={asset.id} 
              className="card-hover cursor-pointer"
              onClick={() => openViewDrawer(asset.id)}
              data-testid={`asset-card-${asset.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <ReadinessDot readiness={asset.readiness} />
                    <GearChip gear={asset.readiness?.gear} />
                    <div>
                      <h3 className="font-semibold text-lg">{asset.unit_number || asset.name}</h3>
                      {asset.name && asset.unit_number && (
                        <p className="text-sm text-muted-foreground">{asset.name}</p>
                      )}
                      <p className="text-xs text-muted-foreground font-mono">{asset.serial_number}</p>
                      {(asset.telemetry_imei || asset.telemetry_source === 'vdaq') && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {asset.telemetry_imei && <ImeiPill imei={asset.telemetry_imei} />}
                          <VdaqPill asset={asset} />
                        </div>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={asset.status} statuses={statuses} />
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Make/Model</span>
                    <span>{asset.make} {asset.model}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Year</span>
                    <span>{asset.year}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Meter</span>
                    <span className="text-right">
                      {formatMeter(asset.hours, asset.hours_unit || 'hours')}
                      {asset.secondary_meter != null && (
                        <span className="block text-[10px] text-muted-foreground">
                          {formatMeter(asset.secondary_meter, asset.secondary_meter_unit || 'hours')}
                        </span>
                      )}
                    </span>
                  </div>
                  {asset.depot_name && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Depot</span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-primary" />
                        {asset.depot_name}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {assets.length < total && (
            <div className="col-span-full flex justify-center py-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadAssets(false)}
                disabled={loadingMore}
                data-testid="assets-load-more-btn-card"
              >
                {loadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Load More ({(total - assets.length).toLocaleString()} more)
              </Button>
            </div>
          )}
        </div>
      ) : (
        /* List View — single-surface bands inside the registry card (Fix 4) */
        <div className="p-0">
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-3 flex-wrap"
                style={{ background: '#FFF8D6', borderBottom: '1px solid #EFE0A0', padding: '9px 14px' }}
                data-testid="assets-bulk-bar">
                <span style={{ fontSize: 12.5, fontWeight: 600, color: '#1A1A1A' }}>
                  {selectedIds.size} asset{selectedIds.size === 1 ? '' : 's'} selected
                </span>
                {[
                  { label: 'Change readiness', tid: 'bulk-change-readiness', admin: true, onClick: () => setBulkDialog('readiness') },
                  { label: 'Change depot', tid: 'bulk-change-depot', admin: true, onClick: () => setBulkDialog('depot') },
                  { label: 'Schedule service', tid: 'bulk-schedule-service', admin: true, onClick: () => setBulkDialog('schedule') },
                  { label: 'Export selection', tid: 'bulk-export-selection', onClick: exportSelection },
                ].filter(b => !b.admin || isAdmin()).map(b => (
                  <button key={b.tid} type="button" onClick={b.onClick}
                    className="hover:bg-[#FFFBEA] transition-colors"
                    style={{ height: 28, background: '#FFFFFF', border: '1px solid #DCCF95', borderRadius: 7, fontSize: 12, padding: '0 10px', color: '#1A1A1A' }}
                    data-testid={b.tid}>
                    {b.label}
                  </button>
                ))}
                <button type="button" onClick={clearSelection}
                  className="ml-auto underline"
                  style={{ fontSize: 12, color: '#59554D' }}
                  data-testid="bulk-clear">
                  Clear
                </button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-center p-4" style={{ width: 30 }} data-testid="assets-select-all-head" />
                    {visibleColumns.has('unit_number')   && <SortableTh sortKey="unit_number"   label="Unit #"        current={sortKey} dir={sortDir} onSort={toggleSort} />}
                    {visibleColumns.has('serial_number') && <SortableTh sortKey="serial_number" label="Serial Number" current={sortKey} dir={sortDir} onSort={toggleSort} />}
                    {visibleColumns.has('category')      && <SortableTh sortKey="category"      label="Category"      current={sortKey} dir={sortDir} onSort={toggleSort} />}
                    {visibleColumns.has('make_model')    && <SortableTh sortKey="make_model"    label="Make/Model"    current={sortKey} dir={sortDir} onSort={toggleSort} />}
                    {visibleColumns.has('year')          && <SortableTh sortKey="year"          label="Year"          current={sortKey} dir={sortDir} onSort={toggleSort} align="right" />}
                    {visibleColumns.has('hours')         && <SortableTh sortKey="hours"         label="Meter"         current={sortKey} dir={sortDir} onSort={toggleSort} align="right" />}
                    {visibleColumns.has('depot')         && <SortableTh sortKey="depot"         label="Depot"         current={sortKey} dir={sortDir} onSort={toggleSort} />}
                    {visibleColumns.has('status')        && <SortableTh sortKey="status"        label="Status"        current={sortKey} dir={sortDir} onSort={toggleSort} />}
                    <th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssets.map((asset, idx) => (
                    <tr 
                      key={asset.id} 
                      data-status={asset.status}
                      data-selected={selectedIds.has(asset.id) ? 'true' : undefined}
                      className={`border-b border-border hover:bg-secondary/50 cursor-pointer transition-colors ${
                        highlightedIdx === idx ? 'bg-[#FFD108]/10 ring-1 ring-inset ring-[#FFD108]/40' : ''
                      }`}
                      onClick={() => openViewDrawer(asset.id)}
                      onMouseEnter={() => setHighlightedIdx(idx)}
                      data-testid={`asset-row-${asset.id}`}
                    >
                      <td className={`${densityClass(density)} text-center`} onClick={(e) => e.stopPropagation()} style={{ width: 30 }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(asset.id)}
                          onChange={() => toggleSelect(asset.id)}
                          className="cursor-pointer accent-[#FFD108]"
                          style={{ width: 14, height: 14 }}
                          data-testid={`asset-select-${asset.id}`}
                          aria-label={`Select ${asset.unit_number || asset.name}`}
                        />
                      </td>
                      <td className={densityClass(density)} data-identity="true">
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <ReadinessDot readiness={asset.readiness} />
                              <GearChip gear={asset.readiness?.gear} />
                              <span className="font-semibold text-[13px] text-[#1A1A1A] dark:text-foreground">{asset.unit_number || asset.name}</span>
                              {(asset.asset_class === 'auxiliary') && (
                                <span className="rounded-sm font-semibold" style={{ fontSize: 9, color: '#5A5650', background: '#F0EDE6', border: '1px solid #E2DED4', padding: '1px 4px' }}>AUX</span>
                              )}
                              {asset.telemetry_imei && (
                                <ImeiPill imei={asset.telemetry_imei} />
                              )}
                              <VdaqPill asset={asset} />
                            </div>
                            {density !== 'compact' && asset.name && asset.unit_number && (
                              <p className="text-xs text-muted-foreground">{asset.name}</p>
                            )}
                            {density !== 'compact' && asset.parent_asset_name && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <LinkIcon className="w-2.5 h-2.5" />{asset.parent_asset_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      {visibleColumns.has('serial_number') && (
                        <td className={densityClass(density)}>
                          {asset.serial_number
                            ? <span className="font-mono text-sm" data-mono="true">{asset.serial_number}</span>
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                      )}
                      {visibleColumns.has('category') && (
                        <td className={densityClass(density)}>
                          <span className="capitalize">{getCategoryLabel(asset.category)}</span>
                        </td>
                      )}
                      {visibleColumns.has('make_model') && (
                        <td className={densityClass(density)}>
                          {(asset.make || asset.model)
                            ? `${asset.make || ''} ${asset.model || ''}`.trim()
                            : <span style={{ color: '#B5AFA3' }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.has('year') && (
                        <td className={`${densityClass(density)} text-right`}>
                          {asset.year
                            ? <span className="font-mono text-[11.5px]" data-mono="true">{asset.year}</span>
                            : <span style={{ color: '#B5AFA3' }}>—</span>}
                        </td>
                      )}
                      {visibleColumns.has('hours') && (
                        <td className={`${densityClass(density)} text-right`}>
                          <div className="leading-tight">
                            <span className="font-mono text-[11.5px]" data-mono="true">{formatMeter(asset.hours, asset.hours_unit || 'hours')}</span>
                            {asset.secondary_meter != null && (
                              <span className="block text-[10px] text-muted-foreground font-mono" data-mono="true">
                                {formatMeter(asset.secondary_meter, asset.secondary_meter_unit || 'hours')}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      {visibleColumns.has('depot') && (
                        <td className={densityClass(density)}>
                          {asset.depot_name ? (
                            <span className="flex items-center gap-1 text-sm">
                              <MapPin className="w-3 h-3 text-primary" />
                              {asset.depot_name}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.has('status') && (
                        <td className={densityClass(density)}>
                          <StatusBadge status={asset.status} statuses={statuses} />
                        </td>
                      )}
                      <td className={`${densityClass(density)} text-right`}>
                        <ActionMenu 
                          actions={[
                            { label: 'View', icon: Eye, onClick: () => openViewDrawer(asset.id) },
                            ...(isAdmin() ? [
                              { label: 'Edit', icon: Edit, onClick: () => openEditDrawer(asset) },
                              { label: 'Delete', icon: Trash2, onClick: () => handleDelete(asset.id), variant: 'destructive' },
                            ] : [])
                          ]}
                          testId={`asset-${asset.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {assets.length < total && (
                <div
                  className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-white"
                  data-testid="assets-load-more-bar"
                >
                  <span className="text-xs text-muted-foreground font-mono">
                    Showing {assets.length.toLocaleString()} of {total.toLocaleString()} assets
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadAssets(false)}
                    disabled={loadingMore}
                    data-testid="assets-load-more-btn"
                  >
                    {loadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Load More ({(total - assets.length).toLocaleString()} more)
                  </Button>
                </div>
              )}
            </div>
        </div>
      )}
      </Card>

      {/* Slide Drawer */}
      <SlideDrawer
        open={drawerOpen}
        onOpenChange={closeDrawer}
        title={getDrawerTitle()}
        titleExtra={drawerMode === 'view' ? (
          <span className="inline-flex items-center text-[11px] text-[#8A857A] border border-[#E2DED4] rounded px-1.5 py-0.5" data-testid="view-only-chip">View only</span>
        ) : null}
        size="lg"
        dismissable={drawerMode === 'view'}
        className="asset-sheet-content"
      >
        {loadingAsset ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : drawerMode === 'add' ? (
          <AssetForm 
            categories={categories}
            statuses={statuses}
            makes={makes}
            models={models}
            depots={depots}
            onSuccess={handleFormSuccess} 
            onCancel={closeDrawer}
            token={token}
          />
        ) : drawerMode === 'edit' && selectedAsset ? (
          <AssetForm 
            asset={selectedAsset}
            categories={categories}
            statuses={statuses}
            makes={makes}
            models={models}
            depots={depots}
            onSuccess={handleFormSuccess} 
            onCancel={closeDrawer}
            token={token}
          />
        ) : drawerMode === 'view' && selectedAsset ? (
          <AssetDetailView 
            asset={selectedAsset}
            inspections={selectedAssetInspections}
            statuses={statuses}
            onEdit={handleSwitchToEdit}
            onDelete={() => handleDelete(selectedAsset.id)}
            onClose={closeDrawer}
            onUpdated={() => loadAssetDetails(selectedAsset.id)}
            token={token}
          />
        ) : null}
      </SlideDrawer>
      <ConfirmDeleteDialog {...dialogProps} />
      {bulkDialog === 'depot' && (
        <BulkDepotDialog open onOpenChange={(o) => !o && setBulkDialog(null)} ids={[...selectedIds]} depots={depots} onDone={onBulkDone} />
      )}
      {bulkDialog === 'readiness' && (
        <BulkReadinessDialog open onOpenChange={(o) => !o && setBulkDialog(null)} ids={[...selectedIds]} onDone={onBulkDone} />
      )}
      {bulkDialog === 'schedule' && (
        <BulkScheduleDialog open onOpenChange={(o) => !o && setBulkDialog(null)} assets={assets.filter(a => selectedIds.has(a.id))} onDone={onBulkDone} />
      )}
    </div>
  );
};

// Keep these for backward compatibility with routes - they redirect to list page
export const AssetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect to list page - drawer will be opened via URL param in future
    navigate('/assets', { replace: true });
  }, [navigate]);
  
  return null;
};

export const AssetFormPage = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Redirect to list page
    navigate('/assets', { replace: true });
  }, [navigate]);
  
  return null;
};
